import { useMemo, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { HelpCircle } from 'lucide-react'
import type { NotebookNote } from '../../../shared/types/notebook'
import { findNote } from '../notebookTree'

/** Replace [[note-id]] wiki links in a string with clickable gold chips */
function injectWikiLinks(
  text: string,
  allNotes: NotebookNote[],
  onNavigate: (id: string) => void,
): ReactNode[] {
  const regex = /\[\[([^\]]+)\]\]/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    const noteId = match[1]
    if (!noteId) continue
    const linked = findNote(allNotes, noteId)
    if (linked) {
      parts.push(
        <button
          key={`link-${match.index}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate(noteId) }}
          className="inline-flex items-center gap-1 bg-gold/10 text-gold border border-gold/20 rounded-full px-2 py-0.5 text-xs font-medium hover:bg-gold/20 transition-colors cursor-pointer align-baseline mx-0.5"
        >
          <span className="text-xs">{linked.icon}</span>
          {linked.title}
        </button>
      )
    } else {
      parts.push(
        <span
          key={`broken-${match.index}`}
          className="inline-flex items-center gap-1 bg-rose/10 text-rose/60 border border-rose/20 rounded-full px-2 py-0.5 text-xs font-medium align-baseline mx-0.5"
        >
          <HelpCircle size={10} />
          <span className="line-through">{noteId}</span>
        </span>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/** Recursively walk React children and replace text containing [[…]] with wiki link chips */
function processWikiLinks(
  children: ReactNode,
  allNotes: NotebookNote[],
  onNavigate: (id: string) => void,
): ReactNode {
  if (typeof children === 'string') {
    if (children.includes('[[')) {
      return <>{injectWikiLinks(children, allNotes, onNavigate)}</>
    }
    return children
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => (
      <span key={i}>{processWikiLinks(child, allNotes, onNavigate)}</span>
    ))
  }
  return children
}

/** Build a line-index map: checkbox occurrence N → source line number */
function buildCheckboxLineMap(md: string): number[] {
  const map: number[] = []
  md.split('\n').forEach((line, i) => {
    if (/^\s*- \[[ x]\] /i.test(line)) map.push(i)
  })
  return map
}

/** Rendered markdown with ReactMarkdown, interactive checkboxes, and wiki links */
export default function RenderedMarkdown({
  content,
  allNotes,
  onNavigate,
  onToggleCheckbox,
}: {
  content: string
  allNotes: NotebookNote[]
  onNavigate: (id: string) => void
  onToggleCheckbox: (lineIndex: number) => void
}) {
  const checkboxLineMap = useMemo(() => buildCheckboxLineMap(content), [content])
  let checkboxCount = 0

  return (
    <div className="text-pearl/80 text-sm leading-relaxed prose-notebook">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Interactive task-list checkboxes
          input(props) {
            if (props.type !== 'checkbox') return <input {...props} />
            const idx = checkboxCount++
            const lineIdx = checkboxLineMap[idx]
            const isChecked = !!props.checked
            return (
              <span
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (lineIdx != null) onToggleCheckbox(lineIdx) }}
                className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded-md border-2 transition-colors cursor-pointer align-middle flex-shrink-0"
                style={{
                  borderColor: isChecked ? 'var(--color-gold)' : 'var(--color-pearl-20, rgba(205,200,190,0.2))',
                  backgroundColor: isChecked ? 'var(--color-gold)' : 'transparent',
                }}
              >
                {isChecked && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#0A0E17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
            )
          },
          // Task list items: checkbox + flex-1 content wrapper. The wrapper lets
          // text wrap normally on narrow screens (without flex shrinking each child
          // — strong, em, text — to a 1-char column). When the meta is on a second
          // line via a markdown hard break, render it as a muted sub-row.
          li(props) {
            const { children, className, ...rest } = props
            const isTask = typeof className === 'string' && className.includes('task-list-item')
            if (!isTask) {
              return <li className={className} {...rest}>{processWikiLinks(children, allNotes, onNavigate)}</li>
            }
            const childArray = Array.isArray(children) ? children : [children]
            const checkbox = childArray[0]
            const restChildren = childArray.slice(1)
            const brIdx = restChildren.findIndex(
              (c) => c != null && typeof c === 'object' && 'type' in c && (c as { type: unknown }).type === 'br',
            )
            return (
              <li
                className="task-list-item flex items-start gap-1 py-1 list-none rounded-lg hover:bg-white/[0.03] transition-colors cursor-pointer"
                {...rest}
              >
                {checkbox}
                <div className="flex-1 min-w-0">
                  {brIdx === -1 ? (
                    processWikiLinks(restChildren, allNotes, onNavigate)
                  ) : (
                    <>
                      <div>{processWikiLinks(restChildren.slice(0, brIdx), allNotes, onNavigate)}</div>
                      <div className="text-xs text-pearl/45 mt-0.5 leading-snug">
                        {processWikiLinks(restChildren.slice(brIdx + 1), allNotes, onNavigate)}
                      </div>
                    </>
                  )}
                </div>
              </li>
            )
          },
          // Process wiki links in paragraphs
          p(props) {
            return <p>{processWikiLinks(props.children, allNotes, onNavigate)}</p>
          },
          // Process wiki links in headings
          h1(props) {
            return <h1>{processWikiLinks(props.children, allNotes, onNavigate)}</h1>
          },
          h2(props) {
            return <h2>{processWikiLinks(props.children, allNotes, onNavigate)}</h2>
          },
          h3(props) {
            return <h3>{processWikiLinks(props.children, allNotes, onNavigate)}</h3>
          },
          // Suppress the default ul class from GFM task lists
          ul(props) {
            const { className, ...rest } = props
            const isTaskList = typeof className === 'string' && className.includes('contains-task-list')
            return <ul className={isTaskList ? 'space-y-0.5 list-none pl-0' : className} {...rest} />
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
